// =============================================================================
// plannerPersistence.ts — persisted milestones + reconcile (v0.2.35, Wave B1)
// =============================================================================
//
// Wave A derived milestones live and never stored them. Wave B1 persists them
// in the timeline_milestones subcollection so status (pending / in-progress /
// complete) can attach.
//
// THE RECONCILE — the careful part. On planner load we:
//   1. Derive what milestones the current scholarships SHOULD produce.
//   2. Compare against what's stored:
//      - should exist, not stored      -> CREATE
//      - stored, should not exist      -> DELETE, but ONLY if untouched
//        (status 'pending' and no manual override). A completed or
//        in-progress milestone is KEPT even if its source changed, so human
//        work is never destroyed.
//      - stored and should exist       -> UPDATE target_date / title if the
//        derivation changed, but NEVER touch status / completed_at.
//
// Guiding rule: derivation owns dates + existence; the human owns status.
// The reconcile is idempotent — once in sync, a re-run writes nothing.
// =============================================================================

import {
  collection, doc, getDocs, writeBatch, serverTimestamp,
} from 'firebase/firestore';
import type { Firestore } from 'firebase/firestore';
import type { TimelineMilestone, MilestoneType } from '@scholarship-app/types';
import type { DerivedMilestone } from './planner';

/** Wave A milestone type slug -> schema MilestoneType enum value. */
const TYPE_MAP: Record<string, MilestoneType> = {
  essay_draft: 'essay_draft_due',
  essay_final: 'essay_final_due',
  rec_request: 'recommendation_request_by',
  transcript: 'transcript_request_by',
  test_scores: 'test_score_send_by',
  portfolio: 'portfolio_due',
  submit: 'scholarship_submit_by',
  // Admission milestones (v0.2.37, Wave B2) — one per admission deadline.
  admission_ea: 'application_submit_by',
  admission_ed: 'application_submit_by',
  admission_rd: 'application_submit_by',
};

/** The Wave A type slug embedded in a derived milestone id (`schId__slug`). */
function slugFromDerivedId(id: string): string {
  const idx = id.indexOf('__');
  return idx >= 0 ? id.slice(idx + 2) : id;
}

function milestoneTypeFor(derived: DerivedMilestone): MilestoneType {
  const fallback: MilestoneType =
    derived.sourceType === 'admission' ? 'application_submit_by' : 'scholarship_submit_by';
  return TYPE_MAP[slugFromDerivedId(derived.id)] ?? fallback;
}

/** Path to an applicant's timeline_milestones collection. */
function milestonesCol(db: Firestore, householdId: string, applicantId: string) {
  return collection(
    db, 'households', householdId, 'applicants', applicantId, 'timeline_milestones'
  );
}

/**
 * Reconcile stored milestones against the freshly-derived set.
 * Returns the up-to-date list of stored milestones (post-reconcile).
 *
 * `derived` is the output of deriveAllMilestones(). The derived milestone id
 * (`{scholarshipId}__{slug}`) is reused verbatim as the Firestore doc id, so
 * the two sides line up by id.
 */
export async function reconcileMilestones(
  db: Firestore,
  householdId: string,
  applicantId: string,
  derived: DerivedMilestone[],
  userId: string
): Promise<TimelineMilestone[]> {
  const col = milestonesCol(db, householdId, applicantId);
  const snap = await getDocs(col);
  const stored = new Map<string, TimelineMilestone>();
  for (const d of snap.docs) stored.set(d.id, d.data() as TimelineMilestone);

  const derivedById = new Map(derived.map((d) => [d.id, d]));
  const batch = writeBatch(db);
  let writes = 0;
  const result: TimelineMilestone[] = [];
  const nowIso = new Date().toISOString();

  // --- CREATE + UPDATE ----------------------------------------------------
  for (const d of derived) {
    const existing = stored.get(d.id);
    if (!existing) {
      // CREATE — a milestone the current data calls for but isn't stored.
      const m: TimelineMilestone = {
        milestone_id: d.id,
        milestone_type: milestoneTypeFor(d),
        title: d.title,
        target_date: d.targetDate,
        status: 'pending',
        derived_from: d.sourceType === 'admission'
          ? {
              type: 'admission',
              school_id: d.scholarshipId,
              anchor_date: d.deadlineDate,
              offset_days: d.daysBeforeDeadline,
              rule_applied: slugFromDerivedId(d.id),
            }
          : {
              type: 'scholarship',
              scholarship_id: d.scholarshipId,
              anchor_date: d.deadlineDate,
              offset_days: d.daysBeforeDeadline,
              rule_applied: slugFromDerivedId(d.id),
            },
        linked_resources: { essay_ids: [], document_ids: [], rec_request_ids: [] },
        overridable: true,
        manual_override_active: false,
        completed_at: null,
        created_at: nowIso,
        updated_at: nowIso,
      };
      batch.set(doc(col, d.id), { ...m, created_at: serverTimestamp(), updated_at: serverTimestamp() });
      writes++;
      result.push(m);
    } else {
      // UPDATE — only the derivation-owned fields, and only if changed.
      // status / completed_at / manual_override_active are NEVER touched here.
      const dateChanged = existing.target_date !== d.targetDate;
      const titleChanged = existing.title !== d.title;
      const anchorChanged = existing.derived_from?.anchor_date !== d.deadlineDate;
      if (dateChanged || titleChanged || anchorChanged) {
        batch.update(doc(col, d.id), {
          target_date: d.targetDate,
          title: d.title,
          'derived_from.anchor_date': d.deadlineDate,
          'derived_from.offset_days': d.daysBeforeDeadline,
          updated_at: serverTimestamp(),
        });
        writes++;
        result.push({
          ...existing,
          target_date: d.targetDate,
          title: d.title,
          derived_from: {
            ...existing.derived_from,
            anchor_date: d.deadlineDate,
            offset_days: d.daysBeforeDeadline,
          },
        });
      } else {
        result.push(existing);
      }
    }
  }

  // --- DELETE -------------------------------------------------------------
  for (const [id, m] of stored) {
    if (derivedById.has(id)) continue; // still wanted — handled above
    // Stored but no longer derived. Delete ONLY if the human hasn't touched
    // it. A completed or in-progress milestone, or one manually overridden,
    // is KEPT — its source scholarship may be gone, but the record of work
    // done should not vanish.
    const untouched =
      m.status === 'pending' && !m.manual_override_active && m.completed_at == null;
    if (untouched) {
      batch.delete(doc(col, id));
      writes++;
    } else {
      result.push(m); // keep it
    }
  }

  if (writes > 0) await batch.commit();
  void userId; // reserved for future audit fields
  result.sort((a, b) => a.target_date.localeCompare(b.target_date));
  return result;
}

/** Set a milestone's status. Completing it stamps completed_at. */
export async function setMilestoneStatus(
  db: Firestore,
  householdId: string,
  applicantId: string,
  milestoneId: string,
  status: TimelineMilestone['status']
): Promise<void> {
  const ref = doc(milestonesCol(db, householdId, applicantId), milestoneId);
  const batch = writeBatch(db);
  batch.update(ref, {
    status,
    completed_at: status === 'complete' ? new Date().toISOString() : null,
    updated_at: serverTimestamp(),
  });
  await batch.commit();
}

/**
 * Set the linked document ids on a milestone (v0.2.37, Wave B2). The planner
 * only *references* Documents entries — upload happens in the Documents tab.
 * Pass the full desired list of document ids; it replaces the existing set.
 */
export async function setMilestoneLinkedDocuments(
  db: Firestore,
  householdId: string,
  applicantId: string,
  milestoneId: string,
  documentIds: string[]
): Promise<void> {
  const ref = doc(milestonesCol(db, householdId, applicantId), milestoneId);
  const batch = writeBatch(db);
  batch.update(ref, {
    'linked_resources.document_ids': documentIds,
    updated_at: serverTimestamp(),
  });
  await batch.commit();
}
